import type {
  AlertsByDate,
  FormattedChannel,
  GetChannelDetailsResponse
} from '@library/types/alerts';
import { useEffect, useMemo, useRef } from 'react';
import { Alert } from '@bo-components/alerts/Alert';
import { DateTime } from 'luxon';

export interface AlertsProps {
  alertsByDate: Array<AlertsByDate>;
  channelDetails: GetChannelDetailsResponse | undefined;
  selectedChannel: FormattedChannel;
  isFetching: boolean;
  specificAlertId?: string;
}

export const Alerts = ({
  alertsByDate,
  channelDetails,
  selectedChannel,
  isFetching,
  specificAlertId = 'none'
}: AlertsProps) => {
  const { id: selectedChannelId } = selectedChannel;
  const today = useMemo(() => DateTime.now(), []);
  const yesterday = useMemo(() => today.minus({ days: 1 }), [today]);

  const alertsRef = useRef<HTMLDivElement | null>(null);
  const alertRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (alertsRef.current) {
      alertsRef.current.scrollTop = alertsRef.current.scrollHeight;

      if (
        specificAlertId &&
        specificAlertId !== 'none' &&
        alertRefs.current.has(specificAlertId)
      ) {
        const alertElement = alertRefs.current.get(specificAlertId);
        if (alertElement) {
          alertElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [specificAlertId, alertsByDate]);

  return (
    <div
      ref={alertsRef}
      className="bg-soFresh-110 text-soClean-110 flex h-full flex-col-reverse gap-2 overflow-scroll p-6 pb-0"
    >
      {alertsByDate &&
        !isFetching &&
        alertsByDate.map((alertsByDateObj) => {
          const alertDate = DateTime.fromISO(alertsByDateObj.date);

          let dateDisplay = alertDate.toFormat('MMMM dd');
          if (today.year !== alertDate.year) {
            dateDisplay += ` ${alertDate.year}`;
          }

          if (today.hasSame(alertDate, 'day')) {
            dateDisplay = 'Today';
          } else if (yesterday.hasSame(alertDate, 'day')) {
            dateDisplay = 'Yesterday';
          }

          return (
            <div
              className="gap-2 p-4"
              key={`alerts for ${alertsByDateObj.date}`}
            >
              <div className="flex items-center gap-2">
                <p className="text-soClean-130 gap-2 text-lg">{dateDisplay}</p>
                <hr className="border-soFresh-130 w-full gap-2 pl-2" />
              </div>
              <div className="flex flex-col-reverse gap-2 pt-5">
                {alertsByDateObj.alerts.map((alertObj) => (
                  <div
                    key={`alert-${alertObj.id}`}
                    ref={(el) => {
                      if (el && alertObj.id) {
                        alertRefs.current.set(alertObj.id, el);
                      }
                    }}
                  >
                    <Alert
                      alert={alertObj}
                      channelDetails={channelDetails}
                      selectedChannelId={selectedChannelId}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};
